import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollManager:
 *  - PUSH (новый раздел) → скролл в начало
 *  - POP (назад/вперёд)  → восстанавливаем позицию из sessionStorage,
 *    активно дожидаясь подгрузки контента через ResizeObserver + interval (до 3 секунд).
 *  - REPLACE → не трогаем
 *
 * Позиция сохраняется НЕ в cleanup useEffect (там она уже может быть искажена
 * автокоррекцией браузера при смене страницы на более короткую), а на каждом
 * скролл-событии (throttled через rAF) — поэтому всегда отражает последнюю
 * реальную позицию пользователя.
 */
export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const cleanupRef = useRef(null);

  // Отключаем встроенный механизм браузера
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Throttled-сохранение позиции при каждом скролле + при unload
  useEffect(() => {
    const capturedPath = location.pathname;
    const capturedSearch = location.search;
    const key = `scroll:${capturedPath}${capturedSearch}`;
    let raf = null;
    const save = () => {
      // Защита: если фактический URL уже изменился (идёт навигация и браузер автокорректит скролл),
      // не перезаписываем сохранённое значение для покидаемой страницы.
      if (window.location.pathname !== capturedPath || window.location.search !== capturedSearch) return;
      try { sessionStorage.setItem(key, String(window.scrollY || 0)); } catch (e) { /* ignore */ }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; save(); });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", save);
    window.addEventListener("pagehide", save);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", save);
      window.removeEventListener("pagehide", save);
    };
  }, [location.pathname, location.search]);

  // Реакция на смену роута
  useEffect(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    const key = `scroll:${location.pathname}${location.search}`;

    if (navType === "POP") {
      const saved = parseInt(sessionStorage.getItem(key) || "0", 10);
      if (saved <= 0) {
        window.scrollTo({ top: 0, behavior: "auto" });
        return undefined;
      }
      let stopped = false;
      let observer = null;
      let timer = null;
      let hardStop = null;

      const finish = () => {
        if (stopped) return;
        stopped = true;
        if (observer) { try { observer.disconnect(); } catch (e) {} observer = null; }
        if (timer) { clearInterval(timer); timer = null; }
        if (hardStop) { clearTimeout(hardStop); hardStop = null; }
      };

      const tryScroll = () => {
        if (stopped) return;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        if (maxScroll >= saved) {
          window.scrollTo({ top: saved, behavior: "auto" });
          finish();
        } else if (maxScroll > 0) {
          // Промежуточный скролл — на максимально возможную позицию
          window.scrollTo({ top: maxScroll, behavior: "auto" });
        }
      };

      tryScroll();
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(tryScroll);
        observer.observe(document.body);
      }
      timer = setInterval(tryScroll, 100);
      hardStop = setTimeout(finish, 3000);
      cleanupRef.current = finish;
      return finish;
    }

    if (navType === "PUSH") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    return undefined;
  }, [location.pathname, location.search, navType]);

  return null;
}
